import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { RequestState } from '../../core/enums/requestState';
import { RootState } from '../../core/store/store';
import { LeadsysPictureFilterFormValue } from '../interfaces/leadsys-picture-filter-form-value.interface';
import { LeadsysPictureFilterItem } from '../interfaces/leadsys-picture-filter-item.interface';
import { LeadsysPictureFilters } from '../interfaces/leadsys-picture-filters.interface';
import { LeadsysPicture } from '../interfaces/leadsys-picture.interface';

export interface LeadsysPictureState {
  readonly requestStates: {
    readonly fetchLeadsysPictureFilters: RequestState;
    readonly fetchLeadsysPictures: RequestState;
    readonly fetchSurveyFilters: RequestState;
    readonly downloadSelectedLeadsysPictures: RequestState;
  };
  readonly leadsysPictureFilters: LeadsysPictureFilters | null;
  readonly leadsysPictures: LeadsysPicture[];
  readonly surveyFilters: readonly LeadsysPictureFilterItem[];
  readonly pageSize: number;
  readonly imagesCount: number;
}

const leadsysPictureState: LeadsysPictureState = {
  requestStates: {
    fetchLeadsysPictureFilters: RequestState.IDLE,
    fetchLeadsysPictures: RequestState.IDLE,
    fetchSurveyFilters: RequestState.IDLE,
    downloadSelectedLeadsysPictures: RequestState.IDLE,
  },
  leadsysPictureFilters: null,
  leadsysPictures: [],
  surveyFilters: [],
  pageSize: 1,
  imagesCount: 0,
};

const leadsysPictureSlice = createSlice({
  name: 'leadsysPicture',
  initialState: leadsysPictureState,
  reducers: {
    fetchLeadsysPictureFilters(state) {
      state.requestStates.fetchLeadsysPictureFilters = RequestState.PENDING;
    },

    fetchLeadsysPictureFiltersSuccess(state, { payload }: PayloadAction<{ leadsysPictureFilters: LeadsysPictureFilters }>) {
      state.requestStates.fetchLeadsysPictureFilters = RequestState.RESOLVED;
      state.leadsysPictureFilters = payload.leadsysPictureFilters;
    },

    fetchLeadsysPictureFiltersError(state, _: PayloadAction<void>) {
      state.requestStates.fetchLeadsysPictureFilters = RequestState.REJECTED;
    },

    fetchSurveyFilters(state, _: PayloadAction<{ typeIds: (string | number)[] }>) {
      state.requestStates.fetchSurveyFilters = RequestState.PENDING;
    },

    fetchSurveyFiltersSuccess(state, { payload }: PayloadAction<{ surveyFilters: LeadsysPictureFilterItem[] }>) {
      state.requestStates.fetchSurveyFilters = RequestState.RESOLVED;
      state.surveyFilters = payload.surveyFilters;
    },

    fetchSurveyFiltersError(state, _: PayloadAction<void>) {
      state.requestStates.fetchSurveyFilters = RequestState.REJECTED;
    },

    fetchLeadsysPictures(state, _: PayloadAction<{ filters: LeadsysPictureFilterFormValue; page: number }>) {
      state.requestStates.fetchLeadsysPictures = RequestState.PENDING;
    },

    fetchLeadsysPicturesSuccess(
      state,
      { payload }: PayloadAction<{ leadsysPictures: LeadsysPicture[]; pageSize: number; imagesCount: number }>
    ) {
      state.requestStates.fetchLeadsysPictures = RequestState.RESOLVED;
      state.pageSize = payload.pageSize;
      state.imagesCount = payload.imagesCount;
      state.leadsysPictures = payload.leadsysPictures;
    },

    fetchLeadsysPicturesError(state, _: PayloadAction<void>) {
      state.requestStates.fetchLeadsysPictures = RequestState.REJECTED;
    },

    downloadLeadsysPictures(state, _: PayloadAction<{ selectedPictures: LeadsysPicture[] }>) {
      state.requestStates.downloadSelectedLeadsysPictures = RequestState.PENDING;
    },

    downloadLeadsysPicturesSuccess(state) {
      state.requestStates.downloadSelectedLeadsysPictures = RequestState.RESOLVED;
    },

    downloadLeadsysPicturesError(state, _: PayloadAction<void>) {
      state.requestStates.downloadSelectedLeadsysPictures = RequestState.REJECTED;
    },

    clearRequestStates(state) {
      state.requestStates.fetchLeadsysPictureFilters = RequestState.IDLE;
      state.requestStates.fetchLeadsysPictures = RequestState.IDLE;
      state.requestStates.fetchSurveyFilters = RequestState.IDLE;
      state.requestStates.downloadSelectedLeadsysPictures = RequestState.IDLE;
      state.leadsysPictureFilters = null;
      state.leadsysPictures = [];
    },
  },
});

export const leadsysPictureActions = leadsysPictureSlice.actions;
export const leadsysPictureReducers = leadsysPictureSlice.reducer;
export const selectLeadsysPictureState = (state: RootState) => state.leadsysPicture;

export const selectLeadsysPictureFilters = (state: RootState) => selectLeadsysPictureState(state).leadsysPictureFilters;
export const selectIsLeadsysPictureFiltersLoading = (state: RootState) =>
  selectLeadsysPictureState(state).requestStates.fetchLeadsysPictureFilters === RequestState.PENDING;

export const selectLeadsysPictures = (state: RootState) => selectLeadsysPictureState(state).leadsysPictures;
export const selectLeadsysPicturePages = (state: RootState) => selectLeadsysPictureState(state).pageSize;
export const selectLeadsysPictureCount = (state: RootState) => selectLeadsysPictureState(state).imagesCount;

export const selectIsLeadsysPicturesLoading = (state: RootState) =>
  selectLeadsysPictureState(state).requestStates.fetchLeadsysPictures === RequestState.PENDING;

export const selectIsLeadsysPicturesResolved = (state: RootState) =>
  selectLeadsysPictureState(state).requestStates.fetchLeadsysPictures === RequestState.RESOLVED;

export const selectSurveyFilters = (state: RootState) => selectLeadsysPictureState(state).surveyFilters;

export const selectIsSurveyFiltersLoading = (state: RootState) =>
  selectLeadsysPictureState(state).requestStates.fetchSurveyFilters === RequestState.PENDING;

export const selectIsSurveyFiltersRejected = (state: RootState) =>
  selectLeadsysPictureState(state).requestStates.fetchSurveyFilters === RequestState.REJECTED;
